import { DonutChart } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { getValueAsPercentage } from "app/pages/keyword-research/keyword-analysis/pages/keywords-overview/serp-composition/serpCompositionUtils";

import { chartConfig } from "./configs/PerformanceAdPlacementChartConfig";
import { Legend } from "./Legend";
import { ShortEmptyState } from "./shortEmptyState";
import {
    StyledBanner,
    BreakdownChartContainer,
    BreakdownChartWithLegendContainer,
    BreakdownLegendWrapper,
    BreakdownChartWrapper,
} from "./StyledComponents";
import type { PerformanceResponse } from "./types";
import { adPlacementConfig, normalizePercentages } from "./utils/graphUtils";

interface IDisplayAdvertisingAdPlacementWidgetProps {
    placement: PerformanceResponse["placements"];
    showEmptyState?: boolean;
    hasError?: boolean;
}

export const DisplayAdvertisingAdPlacementWidget = ({
    placement,
    showEmptyState = false,
    hasError = false,
}: IDisplayAdvertisingAdPlacementWidgetProps) => {
    const rawValues = [placement?.above || 0, placement?.below || 0, placement?.farBelow || 0];
    const normalizedValues = normalizePercentages(rawValues);

    const placementData = adPlacementConfig.map((config, index) => ({
        color: config.color,
        name: translate(config.translationKey),
        id: config.id,
        visible: true,
        y: normalizedValues[index],
        total: normalizedValues[index],
    }));

    const placementLegendItems = adPlacementConfig.map((config, index) => ({
        name: translate(config.translationKey),
        value: getValueAsPercentage(normalizedValues[index]),
        color: config.color,
    }));

    return (
        <Flex
            flexDirection="column"
            height="100%"
        >
            <Flex
                padding="0 24px 6px 24px"
                justifyContent="flex-start"
            >
                <StyledBanner alignItems="center">
                    {translate("ad.intelligence.overview.ad.placement.widget.banner.title")}
                </StyledBanner>
            </Flex>
            <BreakdownChartContainer>
                {showEmptyState ? (
                    <ShortEmptyState isError={!!hasError} />
                ) : (
                    <BreakdownChartWithLegendContainer>
                        <BreakdownChartWrapper>
                            <DonutChart
                                data={[{ data: placementData }]}
                                config={chartConfig}
                                shouldPresentLegend={false}
                                innerSize="55%"
                            />
                        </BreakdownChartWrapper>
                        <BreakdownLegendWrapper>
                            <Legend
                                items={placementLegendItems}
                                dataAutomation="ad-placement-legend"
                            />
                        </BreakdownLegendWrapper>
                    </BreakdownChartWithLegendContainer>
                )}
            </BreakdownChartContainer>
        </Flex>
    );
};
